import { JSX, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ProductDetailsBody } from "./components/ProductDetailsBody";
import { ProductDetailsCarousel } from "./components/ProductDetailsCarousel";
import { useDstProductDetailLabels } from "./hooks/useDstProductDetailLabels";
import { useDstProductDetailData } from "./hooks/useDstProductDetailData";
import { useProductDetailGallery } from "./hooks/useProductDetailGallery";
import { useRedirectOnProductDetailNotFound } from "./hooks/useRedirectOnProductDetailNotFound";
import { showProductCarouselSection } from "./utils/productDetailSectionVisibility";
import { useLocalData } from "../../common/hooks/useLocalData";
import { useAppDispatch } from "../../app/hooks";
import { fetchProductDetails } from "./ProductDetailsSlice";
import { ProductDetailsHeader } from "./components/ProductDetailsHeader";
import { ProductDetailsInfoBar } from "./components/ProductDetailsInfoBar";

const ACCESSORIES_CATEGORY_ID = 139;

const ProductDetailsPageContainer = (): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const { productId } = useParams<{ productId: string }>();
    const { products, categories, getProductById, getLocalImageUrl } =
        useLocalData();
    const { labels } = useDstProductDetailLabels();

    const {
        viewModel,
        accessories,
        similarProducts,
        localImages,
        productCategories,
        isLoading,
        isAssociationsLoading,
        isNotFound,
    } = useDstProductDetailData();

    useEffect(() => {
        if (productId) {
            const accessoriesProducts = products.filter((product) =>
                product.categories.includes(ACCESSORIES_CATEGORY_ID),
            );
            const machines = products.filter(
                (product) =>
                    !product.categories.includes(ACCESSORIES_CATEGORY_ID),
            );

            dispatch(
                fetchProductDetails({
                    productId,
                    categories,
                    machines,
                    accessories: accessoriesProducts,
                    products,
                    getProductById,
                    getLocalImageUrl,
                }),
            );
        }
    }, [
        productId,
        categories,
        products,
        getProductById,
        getLocalImageUrl,
        dispatch,
    ]);

    const isProductNotFound = useRedirectOnProductDetailNotFound({
        isNotFound,
    });

    const openGallery = useProductDetailGallery();

    if (isProductNotFound) {
        return null;
    }

    return (
        <div className="p-product-details">
            <ProductDetailsHeader
                data={viewModel}
                isPageLoading={isLoading}
                labels={labels}
            />

            {showProductCarouselSection(isLoading, localImages.length) && (
                <ProductDetailsCarousel
                    isLoading={isLoading}
                    images={localImages}
                    onImageClick={openGallery}
                />
            )}

            <ProductDetailsInfoBar
                data={viewModel}
                productCategories={productCategories}
                isPageLoading={isLoading}
                labels={labels}
            />

            <ProductDetailsBody
                data={viewModel}
                isPageLoading={isLoading}
                labels={labels}
                accessories={accessories}
                similarProducts={similarProducts}
                isAssociationsLoading={isAssociationsLoading}
            />
        </div>
    );
};

export default ProductDetailsPageContainer;
